import { DateTime } from "luxon";

export const FUNCTIONS_BASE_URL = process.env.NEXT_PUBLIC_FUNCTIONS_URL || "https://us-central1-zamzi-connect.cloudfunctions.net";
export const DEFAULT_TIMEZONE = "Africa/Johannesburg";
export const SLOT_DURATION_MINUTES = 60;
export const MAX_SLOT_COUNT = 300;

export const generateIdempotencyKey = (): string => {
  const raw = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return raw.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);
};

export type AvailabilityHours = Record<string, { start: string; end: string }>;

export type BusyIntervalResponse = {
  id: string;
  start: string;
  end: string;
  source: string;
  status: string;
  allDay: boolean;
  note: string | null;
};

export type AvailabilityResponse = {
  success: boolean;
  profileId: string;
  windowStart?: string;
  windowEnd?: string;
  timezone?: string;
  bufferMinutes?: number;
  minimumNoticeMinutes?: number | null;
  availabilityHours?: AvailabilityHours | null;
  blackoutDates?: string[];
  busy?: BusyIntervalResponse[];
  hasMoreBusyEvents?: boolean;
};

export type SlotDateTime = DateTime;

export type DateTimeRange = { start: DateTime; end: DateTime };

export type SlotOption = {
  id: string;
  start: DateTime;
  end: DateTime;
  disabled: boolean;
};

const parseClock = (value?: string) => {
  if (!value) {
    return null;
  }
  const [hourString, minuteString] = value.split(":");
  const hour = Number(hourString);
  const minute = Number(minuteString);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  return { hour, minute };
};

const toBusyRanges = (availability: AvailabilityResponse | null): DateTimeRange[] => {
  if (!availability) {
    return [];
  }
  const timezone = availability.timezone || DEFAULT_TIMEZONE;
  return (availability.busy ?? []).map((entry) => ({
    start: DateTime.fromISO(entry.start, { zone: "utc" }).setZone(timezone),
    end: DateTime.fromISO(entry.end, { zone: "utc" }).setZone(timezone),
  }));
};

const buildSlotOptions = (availability: AvailabilityResponse | null, busyRanges: DateTimeRange[]): SlotOption[] => {
  if (!availability || !availability.availabilityHours || !Object.keys(availability.availabilityHours).length) {
    return [];
  }

  const timezone = availability.timezone || DEFAULT_TIMEZONE;
  const availabilityHours = availability.availabilityHours || {};
  const blackoutDates = new Set(availability.blackoutDates ?? []);
  const minimumNoticeMinutes = typeof availability.minimumNoticeMinutes === "number" ? availability.minimumNoticeMinutes : 120;
  const slots: SlotOption[] = [];
  const windowEnd = DateTime.fromISO(availability.windowEnd || "", { zone: timezone }).isValid
    ? DateTime.fromISO(availability.windowEnd!, { zone: timezone })
    : DateTime.now().setZone(timezone).plus({ days: 7 });
  let cursorDay = DateTime.fromISO(availability.windowStart || "", { zone: timezone }).isValid
    ? DateTime.fromISO(availability.windowStart!, { zone: timezone }).startOf("day")
    : DateTime.now().setZone(timezone).startOf("day");

  while (cursorDay < windowEnd) {
    const dayName = cursorDay.toFormat("EEEE");
    const window = availabilityHours[dayName];
    cursorDay = cursorDay.plus({ days: 1 });
    if (!window) {
      continue;
    }
    const isoDate = cursorDay.minus({ days: 1 }).toISODate();
    if (isoDate && blackoutDates.has(isoDate)) {
      continue;
    }
    const windowStartClock = parseClock(window.start);
    const windowEndClock = parseClock(window.end);
    if (!windowStartClock || !windowEndClock) {
      continue;
    }

    let slotStart = cursorDay.minus({ days: 1 }).set({
      hour: windowStartClock.hour,
      minute: windowStartClock.minute,
      second: 0,
      millisecond: 0,
    });
    const dayWindowEnd = cursorDay.minus({ days: 1 }).set({
      hour: windowEndClock.hour,
      minute: windowEndClock.minute,
      second: 0,
      millisecond: 0,
    });

    while (slots.length < MAX_SLOT_COUNT) {
      const slotEnd = slotStart.plus({ minutes: SLOT_DURATION_MINUTES });
      if (slotEnd > dayWindowEnd) {
        break;
      }
      // Disable if busy OR violates minimum notice
      const now = DateTime.local().setZone(timezone);
      const violatesMinimumNotice = slotStart < now.plus({ minutes: minimumNoticeMinutes });
      const disabled = busyRanges.some((range) => slotStart < range.end && slotEnd > range.start) || violatesMinimumNotice;
      const slotId = slotStart.toISO() ?? slotStart.toMillis().toString();
      slots.push({
        id: slotId,
        start: slotStart,
        end: slotEnd,
        disabled,
      });
      slotStart = slotStart.plus({ minutes: SLOT_DURATION_MINUTES });
    }
  }

  return slots;
};

const computeIsBusy = (start: DateTime | null, ranges: DateTimeRange[]): boolean => {
  if (!start || !start.isValid) {
    return false;
  }
  const slotEnd = start.plus({ minutes: SLOT_DURATION_MINUTES });
  return ranges.some((range) => start < range.end && slotEnd > range.start);
};

export { toBusyRanges, buildSlotOptions, computeIsBusy };

export const fetchAvailability = async (profileOwnerId: string, signal?: AbortSignal): Promise<AvailabilityResponse> => {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/getAvailability?profileId=${encodeURIComponent(profileOwnerId)}`, {
    method: "GET",
    signal,
  });
  if (!response.ok) {
    // The calendar renders regardless, so the old dead-end wording read as a
    // broken page next to a working date picker. What actually changes when
    // this fails: the host's busy dates are unknown, so dates show as
    // bookable. Saying what is still safe beats announcing a broken page.
    throw new Error(
      "We couldn't check the live calendar just now. You can still pick your dates - we'll confirm real availability with the host before your booking is final.",
    );
  }
  return response.json() as Promise<AvailabilityResponse>;
};

export const launchPayfastCheckout = async (options: {
  profileOwnerId: string;
  bookingId: string;
  accessToken: string;
}): Promise<void> => {
  const response = await fetch("/api/bookings/payfast/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      profileId: options.profileOwnerId,
      bookingId: options.bookingId,
      accessToken: options.accessToken,
    }),
  });

  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    payfastUrl?: string;
    fields?: Record<string, string>;
  };
  if (!response.ok || !payload?.payfastUrl || !payload?.fields || typeof payload.fields !== "object") {
    throw new Error(payload?.error || "Unable to start secure payment.");
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.action = payload.payfastUrl;
  form.style.display = "none";

  Object.entries(payload.fields).forEach(([key, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = String(value || "");
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
};

export const submitReserveSlot = async (options: {
  profileOwnerId: string;
  start: string | null;
  end: string | null;
  customerName: string;
  customerEmail?: string;
  customerWhatsapp?: string;
  note?: string | null;
  metadata: Record<string, unknown>;
}): Promise<{ bookingId: string; accessToken: string | null; status: string | null }> => {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/reserveSlot`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      profileId: options.profileOwnerId,
      start: options.start,
      end: options.end,
      note: options.note ?? null,
      idempotencyKey: generateIdempotencyKey(),
      customerName: options.customerName,
      customerEmail: options.customerEmail || undefined,
      customerWhatsapp: options.customerWhatsapp || undefined,
      metadata: options.metadata,
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.message || "Could not reserve the selected slot.");
  }
  return {
    bookingId: typeof payload?.bookingId === "string" ? payload.bookingId : "",
    accessToken: typeof payload?.accessToken === "string" ? payload.accessToken : null,
    status: typeof payload?.status === "string" ? payload.status : null,
  };
};

export const saveBookingLead = async (options: {
  name: string;
  email: string;
  whatsappNumber?: string;
  profileOwnerId: string;
  bookingId?: string;
  /** The profile whose diary holds the booking (room id for room bookings). */
  bookingProfileId?: string;
  intent: string;
  message: string;
  keepalive?: boolean;
}): Promise<Response> => {
  return fetch("/api/save-lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: options.keepalive,
    body: JSON.stringify({
      name: options.name,
      email: options.email || "",
      whatsappNumber: options.whatsappNumber || "",
      profileOwnerId: options.profileOwnerId,
      bookingId: options.bookingId,
      bookingProfileId: options.bookingProfileId,
      intent: options.intent,
      message: options.message,
    }),
  });
};